import React from 'react'
import { StyledButton } from 'components/Shared/Button/NavButton'
import { NavIconButton } from './styles'
import { ROUTES } from '../Root'
import { useNewTranslation } from 'hooks'
import { useDispatch, useSelector } from 'react-redux'
import Icon from 'components/Icon/Icon'
import ParticleIcon from 'particles/Icon'
import { useLocation, useHistory } from 'react-router-dom'
import styled from 'particles/themes/styled'
import { setConfirmPopup } from 'state/confirmPopup'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import { selectIsInternalUser } from 'state/profile'
import { selectTeamFilter } from 'state/filter'
import { usePermission } from 'hooks/permissions'
import { Ambassador } from 'components/Shared/Permission/enums'

interface NavButtonsProps {
    isExtend?: boolean
    extendOnClick?: () => void
    className?: string
}

const NavButtons: React.FC<NavButtonsProps> = ({ className, isExtend, extendOnClick }) => {
    const currentRoute = useLocation()
    const isInternalUser = useSelector(selectIsInternalUser)
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const teamFilter = useSelector(selectTeamFilter)
    const ambassadorPermission = usePermission(Ambassador.AddRemoveFavourite)

    const closeExtendSideBar = () => {
        if (extendOnClick) {
            extendOnClick()
        }
    }
    const navOnClick = (e: React.MouseEvent<HTMLButtonElement>, direction: string) => {
        e.preventDefault()
        const isCampaignEditorValueChange = localStorage.getItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
        if (isCampaignEditorValueChange) {
            dispatch(
                setConfirmPopup({
                    show: true,
                    onConfirm: () => {
                        history.push(direction)
                    },
                }),
            )
        } else {
            history.push(direction)
        }
    }
    return (
        <div className={className}>
            <div className="nav-buttons">
                <NavIconButton
                    isExtend={isExtend}
                    onClick={closeExtendSideBar}
                    label={t('sidebar.mainMenu.manageTeams')}
                >
                    <StyledButton
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            const path = isInternalUser ? ROUTES.Team : `${ROUTES.Team}/${teamFilter}/members`
                            navOnClick(e, path)
                        }}
                        className="nav-button"
                        width={`${isExtend ? '100%' : '36px'}`}
                        height="36px"
                    >
                        {currentRoute.pathname.includes(ROUTES.TeamDashboard) ||
                        currentRoute.pathname.includes('/team') ? (
                            <Icon.ManageTeamsActiveIcon width={20} height={20} />
                        ) : (
                            <Icon.ManageTeamsIcon width={20} height={20} />
                        )}
                        {isExtend && <div className="button-name">{t('sidebar.mainMenu.manageTeams')}</div>}
                    </StyledButton>
                </NavIconButton>
                {ambassadorPermission && (
                    <NavIconButton
                        isExtend={isExtend}
                        onClick={closeExtendSideBar}
                        label={t('sidebar.mainMenu.manageAmbassadors')}
                    >
                        <StyledButton
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                navOnClick(e, ROUTES.AmbassadorsDashboard)
                            }}
                            className="nav-button"
                            width={`${isExtend ? '100%' : '36px'}`}
                            height="36px"
                        >
                            {currentRoute.pathname.includes(ROUTES.AmbassadorsDashboard) ? (
                                <ParticleIcon name="star" size="20px" form="fill" color="action" />
                            ) : (
                                <ParticleIcon name="star" size="20px" color="neutralAction" />
                            )}
                            {isExtend && <div className="button-name">{t('sidebar.mainMenu.manageAmbassadors')}</div>}
                        </StyledButton>
                    </NavIconButton>
                )}
                <NavIconButton isExtend={isExtend} onClick={closeExtendSideBar} label={t('sidebar.mainMenu.campaigns')}>
                    <StyledButton
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            navOnClick(e, ROUTES.CampaignsDashboard)
                        }}
                        className="nav-button"
                        width={`${isExtend ? '100%' : '36px'}`}
                        height="36px"
                    >
                        {currentRoute.pathname.includes(ROUTES.CampaignsDashboard) ||
                        currentRoute.pathname.includes('/campaign') ? (
                            <Icon.CampaignsActiveIcon width={20} height={20} />
                        ) : (
                            <Icon.CampaignsIcon width={20} height={20} />
                        )}
                        {isExtend && <div className="button-name">{t('sidebar.mainMenu.campaigns')}</div>}
                    </StyledButton>
                </NavIconButton>
                <NavIconButton
                    isExtend={isExtend}
                    onClick={closeExtendSideBar}
                    label={t('sidebar.mainMenu.submissions')}
                >
                    <StyledButton
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            navOnClick(e, ROUTES.SubmissionsDashboard)
                        }}
                        className="nav-button"
                        width={`${isExtend ? '100%' : '36px'}`}
                        height="36px"
                    >
                        {currentRoute.pathname.includes(ROUTES.SubmissionsDashboard) ? (
                            <Icon.PartipostsActiveIcon width={20} height={20} />
                        ) : (
                            <Icon.PartipostsIcon width={20} height={20} />
                        )}

                        {isExtend && <div className="button-name">{t('sidebar.mainMenu.submissions')}</div>}
                    </StyledButton>
                </NavIconButton>
                {isInternalUser && (
                    <NavIconButton
                        isExtend={isExtend}
                        onClick={closeExtendSideBar}
                        label={t('sidebar.mainMenu.banners')}
                    >
                        <StyledButton
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                navOnClick(e, `${ROUTES.Banners}/bannerGroups`)
                            }}
                            className="nav-button"
                            width={`${isExtend ? '100%' : '36px'}`}
                            height="36px"
                        >
                            {currentRoute.pathname.includes(ROUTES.Banners) ? (
                                <Icon.BannersActiveIcon width={20} height={20} />
                            ) : (
                                <Icon.BannersIcon width={20} height={20} />
                            )}

                            {isExtend && <div className="button-name">{t('sidebar.mainMenu.banners')}</div>}
                        </StyledButton>
                    </NavIconButton>
                )}
            </div>
        </div>
    )
}
export default styled(NavButtons)`
    .nav-buttons {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        height: 192px;
        margin-top: 20px;
    }
    .popup-content {
        text-align: center;
    }
`
